/**
 * Applications feature — query + mutation hooks.
 *
 * Canonical tenant-scoped surface:
 *   /api/v1/tenants/{tenant_id}/applications[/...]
 *
 * Hooks take the resolved tenant UUID (not the slug). Pages derive
 * it once from the `/_authed/tenants/$slug` route context and pass
 * it down — slugs never appear in a fetch URL for this feature.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, type ApiError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import { withStepUp } from "@/lib/with-step-up";
import {
  accessPolicySchema,
  publicApplicationEntrySchema,
  applicationSchema,
  applicationsListResponseSchema,
  discoverApplicationResponseSchema,
  entriesToAttributeMap,
  oidcDiscoverySchema,
  appUsageResponseSchema,
  preflightApplicationResponseSchema,
  pilotVerificationSchema,
  rotateSecretResponseSchema,
  type AccessPolicy,
  type Application,
  type ApplicationsListResponse,
  type AppUsageMap,
  type ApplicationsListSearch,
  type ApplicationStatus,
  type CreateApplicationInput,
  type DiscoverApplicationResponse,
  type OidcDiscovery,
  type PreflightApplicationResponse,
  type PilotVerification,
  type Protocol,
  type UpdateAccessPolicyInput,
  type UpdateApplicationInput,
  samlCertSchema,
  samlRotateRequestSchema,
  applicationWorkspaceCapabilitiesSchema,
  type SAMLCert,
  type SAMLRotateRequest,
  nameIDReportSchema,
  type NameIDReport,
} from "./schemas";

function base(tenantID: string): string {
  return `/api/v1/tenants/${encodeURIComponent(tenantID)}/applications`;
}

export function usePublicApplicationEntry(tenantID: string, appId: string) {
  return useQuery({
    queryKey: [...queryKeys.applications.detail(tenantID, appId), "public-entry"],
    queryFn: async () => publicApplicationEntrySchema.parse(await apiFetch<unknown>(`${base(tenantID)}/${encodeURIComponent(appId)}/public-entry`, { cache: "no-store" })),
    staleTime: 0,
    retry: false,
  });
}

export function usePublishApplicationEntry(tenantID: string, appId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (entry: { label: string; url: string } | null) => withStepUp(() => apiFetch<void>(`${base(tenantID)}/${encodeURIComponent(appId)}/public-entry`, {
      method: entry ? "PUT" : "DELETE",
      ...(entry ? { body: entry } : {}),
    })),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.applications.detail(tenantID, appId) }),
  });
}

// ── Fetchers ──────────────────────────────────────────────────────────────

export async function fetchApplicationsList(
  tenantID: string,
  search: ApplicationsListSearch,
): Promise<ApplicationsListResponse> {
  const raw = await apiFetch<unknown>(base(tenantID), {
    searchParams: {
      q: search.q,
      protocol: search.protocol,
      sort_by: search.sortBy,
      sort_order: search.sortOrder,
      offset: search.offset,
    },
  });
  return applicationsListResponseSchema.parse(raw);
}

export async function fetchApplication(
  tenantID: string,
  appId: string,
): Promise<Application> {
  const raw = await apiFetch<unknown>(
    `${base(tenantID)}/${encodeURIComponent(appId)}`,
  );
  return applicationSchema.parse(raw);
}

export async function fetchApplicationWorkspaceCapabilities(tenantID: string, appId: string) {
  const raw = await apiFetch<unknown>(`${base(tenantID)}/${encodeURIComponent(appId)}/workspace-capabilities`);
  return applicationWorkspaceCapabilitiesSchema.parse(raw);
}

// ── Queries ───────────────────────────────────────────────────────────────

/**
 * The tenant's SAML estate by what each application identifies people with.
 *
 * Read-only and tenant-wide: the question — which of my applications send a
 * value somebody else could later hold — is not answerable one application at a
 * time, which is why it went unanswered. Immutable within a session's attention
 * span, so it is not polled.
 */
export function useNameIDReportQuery(tenantID: string) {
  return useQuery<NameIDReport, ApiError>({
    queryKey: queryKeys.applications.nameIDReport(tenantID),
    queryFn: async () =>
      nameIDReportSchema.parse(await apiFetch<unknown>(`${base(tenantID)}/name-identifiers`)),
    enabled: tenantID.length > 0,
    staleTime: 60_000,
  });
}

export function useApplicationsQuery(
  tenantID: string,
  search: ApplicationsListSearch,
) {
  return useQuery<ApplicationsListResponse, ApiError>({
    queryKey: queryKeys.applications.list(tenantID, search),
    queryFn: () => fetchApplicationsList(tenantID, search),
    enabled: tenantID.length > 0,
  });
}

export function useApplicationQuery(tenantID: string, appId: string) {
  return useQuery<Application, ApiError>({
    queryKey: queryKeys.applications.detail(tenantID, appId),
    queryFn: () => fetchApplication(tenantID, appId),
    enabled: tenantID.length > 0 && appId.length > 0,
  });
}

export function useApplicationWorkspaceCapabilitiesQuery(tenantID: string, appId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.productAreas.applicationWorkspace(tenantID, appId),
    queryFn: () => fetchApplicationWorkspaceCapabilities(tenantID, appId),
    enabled,
  });
}

// fetchOidcDiscovery pulls the tenant's live OpenID Connect
// discovery document directly from the IdP host. This is a
// cross-origin GET to the tenant host (e.g.
// `https://acme.pureid.ai/.well-known/openid-configuration`),
// NOT a call to the admin API — so it deliberately does NOT go
// through `apiFetch`:
//
//   - The document is public and unauthenticated; we use
//     `credentials: "omit"` so the admin session cookie is never
//     attached to a cross-origin tenant-host request.
//   - The backend CORS middleware dynamically allows the admin
//     origin (`https://*.{baseDomain}`), so the browser can read
//     the response.
//
// Rendering this live document — rather than re-deriving endpoint
// paths in the UI — is what guarantees the setup panel can never
// drift from what the IdP actually serves.
export async function fetchOidcDiscovery(tenantHost: string): Promise<OidcDiscovery> {
  const url = `https://${tenantHost}/.well-known/openid-configuration`;
  const res = await fetch(url, {
    method: "GET",
    headers: { Accept: "application/json" },
    credentials: "omit",
  });
  if (!res.ok) {
    throw new Error(
      `OIDC discovery fetch failed for ${tenantHost}: ${res.status} ${res.statusText}`,
    );
  }
  const raw: unknown = await res.json();
  return oidcDiscoverySchema.parse(raw);
}

export function useOidcDiscoveryQuery(tenantHost: string) {
  return useQuery<OidcDiscovery, Error>({
    queryKey: queryKeys.applications.oidcDiscovery(tenantHost),
    queryFn: () => fetchOidcDiscovery(tenantHost),
    enabled: tenantHost.length > 0,
    // Discovery is cache-stable — the backend serves it with
    // `Cache-Control: public, max-age=3600`. Mirror that here so
    // the panel reads from cache across the create dialog and the
    // detail page without a redundant network round-trip.
    staleTime: 60 * 60 * 1000,
  });
}

/**
 * Per-application usage: unique users in the last 30 days, and the
 * last access at any time.
 *
 * Applications with NO recorded access are absent from the response
 * rather than present with zeroes, and the table renders that absence
 * as "no data" rather than as a number. The distinction is the whole
 * point of this endpoint: the version of this surface that shipped
 * before it existed rendered "Last active: Never" for every
 * application on every tenant — a claim, not an empty state, and one
 * an operator could act on by deleting a live integration.
 *
 * Both protocols feed it, at different latencies. OIDC records access
 * as it happens; SAML now does too (it previously reached the summary
 * only via the nightly audit-log rebuild, so "last active" could be a
 * rebuild cycle stale on exactly the column an operator reads as a
 * dormancy signal).
 */
export function useAppUsageQuery(tenantID: string) {
  return useQuery<AppUsageMap, ApiError>({
    queryKey: queryKeys.applications.usage(tenantID, "overview"),
    queryFn: async () => {
      // Under the applications path, not a separate /reports tree:
      // the endpoint is gated on applications.read so an operator who
      // can see the list can see its usage columns without also
      // needing users.read.
      const raw = await apiFetch<unknown>(`${base(tenantID)}/usage`);
      const parsed = appUsageResponseSchema.parse(raw);
      const out: AppUsageMap = {};
      for (const app of parsed.apps) {
        out[app.id] = {
          unique_users: app.unique_users,
          last_accessed_at: app.last_accessed_at,
        };
      }
      return out;
    },
    enabled: tenantID.length > 0,
    // Usage is a reporting number, not a live one — a minute of
    // staleness is invisible to the operator and saves a round trip
    // on every return to the list.
    staleTime: 60 * 1000,
  });
}

// ── Mutations ─────────────────────────────────────────────────────────────

// Discovery mutation — POSTs an SP base URL to the backend
// discovery endpoint, returns whatever could be probed. Consumed
// by the create-application dialog's Phase 0. NOT step-up-gated:
// nothing is created until the operator submits the (pre-filled)
// form, and the SSRF guard on the backend is the security
// boundary regardless of the caller's session strength.
// Preflight is intentionally a MUTATION rather than a query, even
// though it's a GET on the wire. The reasoning:
//
//   1. Operators trigger it explicitly ("Run preflight") — there
//      is no scenario where stale, automatically-refreshed
//      preflight results are useful. A query that auto-runs on
//      mount would burn HEAD requests against the SP every time
//      the detail page is opened.
//   2. Reachability checks are network-state dependent. A preflight
//      run from 60 seconds ago is potentially stale by the time
//      the operator reads it; the mutation pattern surfaces the
//      "click to recheck" affordance that the UI needs anyway.
//   3. The result is not cached because a successful preflight
//      offers no information value over time — the operator
//      either reads it, fixes the noted issues, and re-runs, or
//      navigates away and the data is irrelevant.
//
// Why GET (not POST) on the wire despite the side-effect concern:
// preflight makes no modifications to PureID state. It reads the
// application row and issues HEAD probes against URLs already
// stored on that row (the operator wrote them in via update). The
// HEAD probes ARE side effects against external systems, but
// those URLs are operator-controlled, behind a permission gate,
// and produce no new privilege the operator didn't already have.
// GET-with-mutation is the right shape: the URL has no body
// (path-only), the result is uncached, and the audit row makes
// the side effect tamper-evidently visible. POST would have
// required either a meaningless body or a wire shape divergent
// from the rest of the read subtree.
//
// No step-up: preflight does not modify state and does not exchange
// credentials with the SP. The SSRF-safe HTTP client on the
// backend is the security boundary regardless of session strength.
export function usePreflightApplicationMutation(tenantID: string, appId: string) {
  const qc = useQueryClient();
  return useMutation<PreflightApplicationResponse, ApiError, void>({
    mutationFn: () =>
      apiFetch<unknown>(
        `${base(tenantID)}/${encodeURIComponent(appId)}/preflight`,
        { method: "POST" },
      ).then((raw) => preflightApplicationResponseSchema.parse(raw)),
    onSuccess: (result) => {
      qc.setQueryData(queryKeys.applications.configurationCheck(tenantID, appId), result);
	  qc.invalidateQueries({ queryKey: queryKeys.applications.detail(tenantID, appId) });
    },
  });
}

export function useLatestPreflightQuery(tenantID: string, appId: string) {
  return useQuery<PreflightApplicationResponse | null, ApiError>({
    queryKey: queryKeys.applications.configurationCheck(tenantID, appId),
    queryFn: async () => {
      try {
        const raw = await apiFetch<unknown>(`${base(tenantID)}/${encodeURIComponent(appId)}/preflight/latest`);
        return preflightApplicationResponseSchema.parse(raw);
      } catch (err) {
        if ((err as ApiError)?.status === 404) return null;
        throw err;
      }
    },
    enabled: tenantID.length > 0 && appId.length > 0,
  });
}

export function usePilotVerificationQuery(tenantID: string, appId: string, enabled: boolean) {
  return useQuery<PilotVerification, ApiError>({
    queryKey: queryKeys.applications.pilotVerification(tenantID, appId),
    queryFn: async () => {
      const raw = await apiFetch<unknown>(`${base(tenantID)}/${encodeURIComponent(appId)}/pilot-verification`);
      return pilotVerificationSchema.parse(raw);
    },
    enabled: enabled && tenantID.length > 0 && appId.length > 0,
    refetchInterval: 10_000,
  });
}

export function useDiscoverApplicationMutation(tenantID: string) {
  return useMutation<DiscoverApplicationResponse, ApiError, { url: string }>({
    mutationFn: ({ url }) =>
      apiFetch<unknown>(`${base(tenantID)}/discover`, {
        method: "POST",
        body: { url },
      }).then((raw) => discoverApplicationResponseSchema.parse(raw)),
  });
}

export function useCreateApplicationMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<Application, ApiError, CreateApplicationInput>({
    mutationFn: (input) =>
      withStepUp(() =>
        apiFetch<unknown>(base(tenantID), {
          method: "POST",
          body: input,
        }).then((raw) => applicationSchema.parse(raw)),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.applications.all });
    },
  });
}

export function useUpdateApplicationMutation(
  tenantID: string,
  appId: string,
  protocol: Protocol | undefined,
) {
  const qc = useQueryClient();
  return useMutation<Application, ApiError, UpdateApplicationInput>({
    mutationFn: (input) =>
      withStepUp(() =>
        apiFetch<unknown>(`${base(tenantID)}/${encodeURIComponent(appId)}`, {
          method: "PATCH",
          // saml_attribute_map is edited as an array of {name, source}
          // rows client-side (see schemas.ts) but the wire shape is a
          // plain Record<string, string> — convert at this boundary.
          //
          // Only meaningful for SAML apps. The form always carries a
          // saml_attribute_map field (empty for OIDC/SMART apps, since
          // appToFormValues derives it from app.saml_attribute_map,
          // which the backend omits for non-SAML protocols) — sending
          // it unconditionally would PATCH an empty map onto every
          // OIDC/SMART app edit and surface a misleading
          // "saml_attribute_map": {} entry in that app's update audit
          // event, even though the operator never touched SAML
          // settings. `undefined` here is dropped by JSON.stringify,
          // so the key is omitted from the wire payload entirely
          // rather than sent as an explicit "clear the map" no-op.
          body: {
            ...input,
            saml_attribute_map:
              protocol === "saml" ? entriesToAttributeMap(input.saml_attribute_map) : undefined,
            // Same class of audit noise as saml_attribute_map above:
            // the form always carries a saml_name_id_source field
            // (empty for OIDC/SMART apps), and the backend records
            // saml_name_id_source in the update audit metadata
            // whenever the key is present at all — even as "" — so
            // sending it unconditionally logged a
            // "saml_name_id_source": "" entry on every OIDC/SMART app
            // edit. Omit the key entirely for non-SAML apps.
            saml_name_id_source: protocol === "saml" ? input.saml_name_id_source : undefined,
          },
        }).then((raw) => applicationSchema.parse(raw)),
      ),
    onSuccess: (updated) => {
      qc.setQueryData(
        queryKeys.applications.detail(tenantID, appId),
        updated,
      );
      qc.invalidateQueries({ queryKey: queryKeys.applications.all });
    },
  });
}

/**
 * useRotateClientSecretMutation mints a new client secret and
 * returns the plaintext ONCE.
 *
 * Three console surfaces told the operator to "use Rotate secret
 * on the detail page" to recover a lost secret — the close
 * confirmation, the one-time-reveal banner, and the detail page's
 * own copy — while only the backend endpoint existed. An operator
 * who closed the handoff panel too early was directed to a control
 * that was not there, and their only real recovery was to delete
 * the application and start over.
 *
 * The plaintext lives in the resolved promise value and NOWHERE
 * else: no setQueryData, no cache write. onSuccess invalidates the
 * list so `has_client_secret` and timestamps refresh, and that is
 * all it touches.
 */
export function useRotateClientSecretMutation(tenantID: string, appId: string) {
  const qc = useQueryClient();
  return useMutation<string, ApiError, void>({
    mutationFn: () =>
      withStepUp(() =>
        apiFetch<unknown>(
          `${base(tenantID)}/${encodeURIComponent(appId)}/rotate-secret`,
          { method: "POST" },
        ).then((raw) => rotateSecretResponseSchema.parse(raw).client_secret),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.applications.all });
    },
  });
}

// useSetApplicationStatusMutation flips an application between
// `active` and `disabled` via the dedicated lifecycle endpoint
// (PATCH /applications/{id}/status). This is deliberately separate
// from useUpdateApplicationMutation: the server rejects a `status`
// field on the general update so the transition gets its own audit
// event. Step-up gated like every other application mutation —
// disabling an app stops every login through it.
export function useSetApplicationStatusMutation(tenantID: string, appId: string) {
  const qc = useQueryClient();
  return useMutation<Application, ApiError, { status: ApplicationStatus; exceptionReason?: string }>({
	mutationFn: ({ status, exceptionReason }) =>
      withStepUp(() =>
        apiFetch<unknown>(`${base(tenantID)}/${encodeURIComponent(appId)}/status`, {
          method: "PATCH",
		  body: { status, ...(exceptionReason ? { exception_reason: exceptionReason } : {}) },
        }).then((raw) => applicationSchema.parse(raw)),
      ),
    onSuccess: (updated) => {
      qc.setQueryData(queryKeys.applications.detail(tenantID, appId), updated);
      qc.invalidateQueries({ queryKey: queryKeys.applications.all });
    },
    onError: () => {
      // A timeout or conflict can leave the caller unsure whether another
      // lifecycle command committed. Refresh the server-authored state before
      // offering another decision.
      qc.invalidateQueries({ queryKey: queryKeys.applications.detail(tenantID, appId) });
    },
  });
}

// ── Icon upload / delete ─────────────────────────────────────────────────
//
// Uploads PUT /api/v1/tenants/{tid}/applications/{id}/icon with a
// multipart form carrying a single "file" field. Unlike the other
// mutations, icons don't ever hit the query cache as cached bytes
// — the browser caches via the served-endpoint's ETag. After a
// successful upload, we just invalidate the application detail
// query so the `has_icon` + `icon_updated_at` fields refresh and
// the edit dialog's preview reloads with the new cache-bust
// suffix. No step-up (icons are branding, not a credential).

export interface IconUploadResponse {
  has_icon: true;
  icon_updated_at: string;
  icon_mime: string;
  icon_size_bytes: number;
}

// uploadIconDirect — non-hook icon upload for the post-create
// favicon-from-discovery flow. The hook variant below captures
// (tenantID, appId) at hook init time and is the right shape for
// the detail page where the appId is stable across the form's
// lifetime; the create dialog only knows appId AFTER the create
// resolves, which makes the hook awkward (placeholder UUIDs +
// re-render races). This direct path is the simpler primitive:
// build a FormData, fetch, return the parsed response. The detail
// page hook still wraps it under TanStack Query for cache
// invalidation; here, the caller handles errors directly.
export async function uploadIconDirect(
  tenantID: string,
  appId: string,
  fileBlob: Blob,
  fileName: string,
): Promise<IconUploadResponse> {
  const form = new FormData();
  form.append("file", fileBlob, fileName);
  return apiFetch<IconUploadResponse>(
    `${base(tenantID)}/${encodeURIComponent(appId)}/icon`,
    { method: "PUT", body: form },
  );
}

export function useUploadIconMutation(tenantID: string, appId: string) {
  const qc = useQueryClient();
  return useMutation<IconUploadResponse, ApiError, File>({
    mutationFn: (file) =>
      withStepUp(() => {
        const form = new FormData();
        form.append("file", file, file.name);
        return apiFetch<IconUploadResponse>(
          `${base(tenantID)}/${encodeURIComponent(appId)}/icon`,
          { method: "PUT", body: form },
        );
      }),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: queryKeys.applications.detail(tenantID, appId),
      });
      qc.invalidateQueries({ queryKey: queryKeys.applications.all });
    },
  });
}

export function useDeleteIconMutation(tenantID: string, appId: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, void>({
    mutationFn: () =>
      withStepUp(() =>
        apiFetch<void>(
          `${base(tenantID)}/${encodeURIComponent(appId)}/icon`,
          { method: "DELETE" },
        ),
      ),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: queryKeys.applications.detail(tenantID, appId),
      });
      qc.invalidateQueries({ queryKey: queryKeys.applications.all });
    },
  });
}

export function useDeleteApplicationMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (appId) =>
      withStepUp(() =>
        apiFetch<void>(`${base(tenantID)}/${encodeURIComponent(appId)}`, {
          method: "DELETE",
        }),
      ),
    onSuccess: (_res, appId) => {
      qc.removeQueries({
        queryKey: queryKeys.applications.detail(tenantID, appId),
      });
      qc.invalidateQueries({ queryKey: queryKeys.applications.all });
    },
  });
}

// ── Per-app JIT request policy ─────────────────────────────────
//
// GET / PUT /api/v1/tenants/{id}/identity/hygiene/access-policy/{appId}.
// The backend returns defaulted values when no sidecar row exists,
// so the UI never has to distinguish "configured" vs "default" —
// the form always sees a complete shape.

function policyBase(tenantID: string, appId: string) {
  return `/api/v1/tenants/${encodeURIComponent(tenantID)}/identity/hygiene/access-policy/${encodeURIComponent(appId)}`;
}

export async function fetchAccessPolicy(tenantID: string, appId: string) {
  const raw = await apiFetch<unknown>(policyBase(tenantID, appId));
  return accessPolicySchema.parse(raw);
}

export function useAccessPolicyQuery(tenantID: string, appId: string) {
  return useQuery<AccessPolicy, ApiError>({
    queryKey: ["application-access-policy", tenantID, appId],
    queryFn: () => fetchAccessPolicy(tenantID, appId),
    enabled: tenantID.length > 0 && appId.length > 0,
    staleTime: 30_000,
  });
}

export function useUpdateAccessPolicyMutation(tenantID: string, appId: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, UpdateAccessPolicyInput>({
    mutationFn: (input) =>
      withStepUp(() =>
        apiFetch<void>(policyBase(tenantID, appId), {
          method: "PUT",
          body: input,
        }),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["application-access-policy", tenantID, appId] });
    },
  });
}

// ── SAML IdP signing certificate ─────────────────────────────────────────

const samlCertBase = (tenantID: string, appId: string) =>
  `/api/v1/tenants/${encodeURIComponent(tenantID)}/applications/${encodeURIComponent(appId)}/saml`;

/**
 * useSAMLCertQuery fetches the active IdP signing certificate for a SAML
 * application. Returns undefined while loading and null on a 404 (not yet
 * provisioned). Any other error surfaces as a thrown ApiError.
 */
export function useSAMLCertQuery(
  tenantID: string,
  appId: string,
): { cert: SAMLCert | null | undefined; isLoading: boolean; error: ApiError | null } {
  const { data, isLoading, error } = useQuery<SAMLCert | null, ApiError>({
    queryKey: queryKeys.applications.samlCert(tenantID, appId),
    queryFn: async () => {
      try {
        const raw = await apiFetch(`${samlCertBase(tenantID, appId)}/cert`);
        return samlCertSchema.parse(raw);
      } catch (e) {
        if ((e as ApiError)?.status === 404) return null;
        throw e;
      }
    },
    enabled: Boolean(tenantID && appId),
  });
  return { cert: data, isLoading, error: error as ApiError | null };
}

/**
 * useRotateSAMLCertMutation rotates (or initially provisions) the IdP signing
 * certificate for a SAML application.
 *
 * The mutation is step-up gated — if the server responds with a
 * 401/step-up challenge the ceremony is surfaced and the request is
 * re-issued after re-authentication.
 *
 * On success the cert cache for the affected app is invalidated so the
 * detail card picks up the new key material.
 */
export function useRotateSAMLCertMutation(tenantID: string, appId: string) {
  const qc = useQueryClient();
  return useMutation<SAMLCert, ApiError, SAMLRotateRequest>({
    mutationFn: (input: SAMLRotateRequest) =>
      withStepUp(() =>
        apiFetch<unknown>(`${samlCertBase(tenantID, appId)}/cert/rotate`, {
          method: "POST",
          body: samlRotateRequestSchema.parse(input),
        }).then((raw) => samlCertSchema.parse(raw)),
      ),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: queryKeys.applications.samlCert(tenantID, appId),
      });
    },
  });
}
